import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useProperty } from '@/context/PropertyContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Hotel, 
  Home,
  LayoutDashboard, 
  Calendar, 
  Bookmark,
  LogOut,
  Menu,
  X,
  Wifi,
  Shield,
  Bed,
  ArrowDownUp,
  Package,
  FileText,
  BarChart3,
  ClipboardCheck,
  Settings,
  UserCircle,
  ChevronDown,
  ChevronRight,
  Building,
  DollarSign,
  Users,
  Calculator,
  BookImage,
  LockKeyhole,
  Clock,
  Layers,
  MessageSquare
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DashboardLayout({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const { user, logout } = useAuth();
  const globalProperty = useProperty();
  const location = useLocation();
  const navigate = useNavigate();

  // Shift guard state
  const [shiftChecked, setShiftChecked] = useState(false);
  const [activeShift, setActiveShift] = useState(null);
  const [shiftPropertyId, setShiftPropertyId] = useState(null);
  const [shiftNotas, setShiftNotas] = useState('');
  const [openingShift, setOpeningShift] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isAdmin = user?.role === 'admin';
  const pmsType = user?.pms_type || 'hotel'; // 'hotel' or 'apartamento'
  const isApartment = pmsType === 'apartamento';

  const toggleMenu = (menuName) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  const isActive = (href) => location.pathname === href;
  const isMenuActive = (items) => items?.some(item => location.pathname === item.href);

  // ==================== HOTEL NAVIGATION ====================
  const hotelNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, testId: 'nav-dashboard', moduleKey: 'dashboard' },
    { name: 'Calendario', href: '/calendar', icon: Calendar, testId: 'nav-calendar', moduleKey: 'calendario' },
    { name: 'Multi-Calendario', href: '/multi-calendario', icon: Layers, testId: 'nav-multi-cal', moduleKey: 'multi_calendario' },
    { name: 'Reservas', href: '/reservations', icon: Bookmark, testId: 'nav-reservations', moduleKey: 'reservas' },
    { name: 'Habitaciones', href: '/habitaciones', icon: Bed, testId: 'nav-habitaciones', moduleKey: 'habitaciones' },
    { 
      name: 'Ingresos/Egresos', 
      icon: ArrowDownUp, 
      testId: 'nav-ingresos-egresos',
      moduleKey: 'ingresos_egresos',
      subItems: [
        { name: 'Resumen', href: '/ingresos-egresos' },
        { name: 'Registrar Ingreso', href: '/ingresos-egresos/ingreso' },
        { name: 'Registrar Gasto', href: '/ingresos-egresos/gasto' },
        { name: 'Turnos de Caja', href: '/ingresos-egresos/turnos' },
      ]
    },
    { 
      name: 'Productos y Servicios', 
      icon: Package, 
      testId: 'nav-productos-servicios',
      moduleKey: 'productos_servicios',
      subItems: [
        { name: 'Bodega / Inventario', href: '/bodega' },
      ]
    },
    { 
      name: 'Facturación', 
      icon: FileText, 
      testId: 'nav-facturacion',
      moduleKey: 'facturacion',
      subItems: [
        { name: 'Remisiones', href: '/facturacion/remisiones' },
        { name: 'Facturas DIAN', href: '/facturacion/facturas' },
        { name: 'Configuración', href: '/facturacion/configuracion' },
      ]
    },
    { 
      name: 'Informes', 
      icon: BarChart3, 
      testId: 'nav-informes',
      moduleKey: 'informes',
      subItems: [
        { name: 'General', href: '/informes' },
        { name: 'Ingresos por Reservas', href: '/informes/ingresos-ota' },
        { name: 'Cartera Clientes Preferenciales', href: '/cartera-clientes' },
        { name: 'Cuentas por Cobrar', href: '/cuentas-por-cobrar' },
      ]
    },
    { 
      name: 'Auditorías', 
      icon: ClipboardCheck, 
      testId: 'nav-auditorias',
      href: '/auditorias',
      moduleKey: 'auditorias',
      subItems: []
    },
    { 
      name: 'Ajustes', 
      icon: Settings, 
      testId: 'nav-ajustes',
      moduleKey: 'ajustes',
      subItems: [
        { name: 'Comisiones OTA', href: '/ajustes/comisiones' },
        { name: 'Tarifas', href: '/rates' },
        { name: 'Channel Manager', href: '/channex' },
        { name: 'WhatsApp Bot', href: '/whatsapp' },
        { name: 'Catálogo', href: '/catalog' },
        { name: 'Propiedad', href: '/ajustes/propiedad' },
      ]
    },
    { 
      name: 'Usuario en curso', 
      icon: UserCircle, 
      testId: 'nav-usuario-curso',
      moduleKey: 'usuario_curso',
      subItems: []
    },
    { name: 'Conexiones OTA', href: '/ota-connections', icon: Wifi, testId: 'nav-ota', moduleKey: 'conexiones_ota' },
    { name: 'Simulador Tarifas', href: '/simulador-tarifas', icon: Calculator, testId: 'nav-simulador', moduleKey: 'simulador_tarifas' },
    { name: 'Gestión Usuarios', href: '/usuarios', icon: Users, testId: 'nav-usuarios', moduleKey: 'gestion_usuarios' },
  ];

  // ==================== APARTAMENTOS NAVIGATION ====================
  const apartamentoNavigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, testId: 'nav-dashboard' },
    { name: 'Calendario Unificado', href: '/calendario-unificado', icon: Calendar, testId: 'nav-calendario-unificado' },
    { name: 'Mis Apartamentos', href: '/apartamentos', icon: Building, testId: 'nav-apartamentos' },
    { name: 'Reservas', href: '/reservations', icon: Bookmark, testId: 'nav-reservations' },
    { 
      name: 'Ingresos/Egresos', 
      icon: ArrowDownUp, 
      testId: 'nav-ingresos-egresos',
      subItems: [
        { name: 'Resumen', href: '/ingresos-egresos' },
        { name: 'Registrar Ingreso', href: '/ingresos-egresos/ingreso' },
        { name: 'Registrar Gasto', href: '/ingresos-egresos/gasto' },
      ]
    },
    { 
      name: 'Tarifas', 
      icon: DollarSign, 
      testId: 'nav-tarifas',
      href: '/tarifas'
    },
    { 
      name: 'Huéspedes', 
      icon: Users, 
      testId: 'nav-huespedes',
      href: '/huespedes'
    },
    { 
      name: 'Informes', 
      icon: BarChart3, 
      testId: 'nav-informes',
      subItems: [
        { name: 'General', href: '/informes' },
        { name: 'Ingresos por Reservas', href: '/informes/ingresos-ota' },
        { name: 'Cartera Clientes Preferenciales', href: '/cartera-clientes' },
        { name: 'Cuentas por Cobrar', href: '/cuentas-por-cobrar' },
      ]
    },
    { 
      name: 'Ajustes', 
      icon: Settings, 
      testId: 'nav-ajustes',
      subItems: [
        { name: 'Comisiones OTA', href: '/ajustes/comisiones' },
        { name: 'Mis Propiedades', href: '/ajustes/propiedades' },
        { name: 'WhatsApp Bot', href: '/whatsapp' },
        { name: 'Catálogo', href: '/catalog' },
      ]
    },
    { name: 'Conexiones OTA', href: '/ota-connections', icon: Wifi, testId: 'nav-ota' },
    { name: 'Simulador Tarifas', href: '/simulador-tarifas', icon: Calculator, testId: 'nav-simulador' },
    { name: 'Gestión Usuarios', href: '/usuarios', icon: Users, testId: 'nav-usuarios' },
  ];

  // Select navigation based on pms_type and role
  const isSubUser = user?.role === 'propietario' || user?.role === 'limitado';
  const baseNav = isApartment ? apartamentoNavigation : hotelNavigation;
  
  // Module permissions state
  const [modulePerms, setModulePerms] = useState(null);
  
  useEffect(() => {
    if (isSubUser) {
      const token = localStorage.getItem('token');
      axios.get(`${BACKEND_URL}/api/auth/my-permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        setModulePerms(res.data.module_permissions);
      }).catch(() => {});
    }
  }, [isSubUser]);
  
  // Filter navigation: admin/owner sees all; sub-users filtered by module_permissions
  const ownerNavigation = (() => {
    let nav = baseNav;
    if (isSubUser) {
      nav = nav.filter(item => item.testId !== 'nav-usuarios');
      // If module_permissions is set (not null), filter by it
      if (modulePerms && Array.isArray(modulePerms)) {
        nav = nav.filter(item => !item.moduleKey || modulePerms.includes(item.moduleKey));
      }
    }
    return nav;
  })();

  // Check active shift on mount
  useEffect(() => {
    if (isAdmin) { setShiftChecked(true); return; }
    const checkShift = async () => {
      try {
        const token = localStorage.getItem('token');
        // Get user's properties first
        const propsRes = await axios.get(`${BACKEND_URL}/api/properties`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const props = propsRes.data;
        if (props.length > 0) {
          const pid = props[0].id;
          setShiftPropertyId(pid);
          const shiftRes = await axios.get(`${BACKEND_URL}/api/shifts/${pid}/active`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setActiveShift(shiftRes.data.active ? shiftRes.data.shift : null);
        }
      } catch (e) {
        console.error('Error checking shift:', e);
      } finally {
        setShiftChecked(true);
      }
    };
    checkShift();
  }, [isAdmin]);

  const handleOpenShift = async () => {
    if (!shiftPropertyId) return;
    setOpeningShift(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/shifts/open`,
        { property_id: shiftPropertyId, notas: shiftNotas },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActiveShift(res.data);
      setShiftNotas('');
      toast.success('Turno abierto correctamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al abrir turno');
    } finally {
      setOpeningShift(false);
    }
  };

  const handleCloseShift = async () => {
    if (!activeShift) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/shifts/${activeShift.id}/close`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActiveShift(null);
      toast.success('Turno cerrado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar turno');
    }
  };

  // Admin only navigation
  const adminNavigation = [
    { name: 'Panel Admin', href: '/admin', icon: Shield, testId: 'nav-admin' },
  ];

  const renderMenuItem = (item, index) => {
    const Icon = item.icon;
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isExpanded = expandedMenus[item.name];
    const active = item.href ? isActive(item.href) : isMenuActive(item.subItems);

    // If it has subItems, render as expandable
    if (item.subItems !== undefined) {
      // If no subItems but has href, render as direct link
      if (!hasSubItems && item.href) {
        return (
          <Link
            key={item.name}
            to={item.href}
            data-testid={item.testId}
            className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all duration-200 ${
              active
                ? 'bg-white/10 text-white'
                : 'text-white/70 hover:bg-white/5 hover:text-white'
            }`}
          >
            <Icon className="h-5 w-5" />
            <span className="font-medium">{item.name}</span>
          </Link>
        );
      }
      return (
        <div key={item.name}>
          <button
            onClick={() => hasSubItems ? toggleMenu(item.name) : null}
            data-testid={item.testId}
            className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-md transition-all duration-200 ${
              active
                ? 'bg-white/10 text-white'
                : 'text-white/70 hover:bg-white/5 hover:text-white'
            } ${!hasSubItems ? 'opacity-60 cursor-default' : 'cursor-pointer'}`}
          >
            <div className="flex items-center gap-3">
              <Icon className="h-5 w-5" />
              <span className="font-medium">{item.name}</span>
            </div>
            {hasSubItems && (
              isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
            )}
            {!hasSubItems && (
              <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded">Próx.</span>
            )}
          </button>
          
          {/* Sub-items */}
          {hasSubItems && isExpanded && (
            <div className="ml-4 mt-1 space-y-1 border-l border-white/20 pl-4">
              {item.subItems.map((subItem) => (
                <Link
                  key={subItem.href}
                  to={subItem.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-all ${
                    isActive(subItem.href)
                      ? 'bg-white/10 text-white'
                      : 'text-white/60 hover:bg-white/5 hover:text-white'
                  }`}
                >
                  {subItem.name}
                </Link>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Regular link item
    return (
      <Link
        key={item.name}
        to={item.href}
        data-testid={item.testId}
        onClick={() => setSidebarOpen(false)}
        className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all duration-200 ${
          active
            ? 'bg-white/10 text-white'
            : 'text-white/70 hover:bg-white/5 hover:text-white'
        }`}
      >
        <Icon className="h-5 w-5" />
        <span className="font-medium">{item.name}</span>
      </Link>
    );
  };

  // Determine logo/branding based on pms_type
  const LogoIcon = isApartment ? Home : Hotel;
  const brandName = isApartment ? 'Edén' : 'Edén';
  const brandColor = 'from-slate-800 to-slate-900';

  return (
    <div className="min-h-screen bg-background flex">
      {/* Mobile menu button */}
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-primary rounded-md text-white"
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-gradient-to-b ${brandColor} transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-white/10">
            <Link to="/" className="flex items-center gap-2">
              <img src="https://static.prod-images.emergentagent.com/jobs/66419162-0afe-4996-ab1a-5d519d29062e/images/1419e719ba240b6e00d42093e097a0701e8eac9d9c7b54db9859b4af59d92017.png" alt="Edén" className="h-8 w-8 rounded" />
              <span className="font-bold text-xl text-white" style={{fontFamily: 'Manrope'}}>
                {brandName}
              </span>
            </Link>
            {/* PMS Type Badge */}
            <div className="mt-2">
              <Badge className={`text-xs bg-sky-400/20 text-sky-200`}>
                {isApartment ? 'Módulo Apartamentos' : 'Módulo Hotel'}
              </Badge>
            </div>
          </div>

          {/* Global Property Selector */}
          {!isAdmin && globalProperty?.properties?.length > 0 && (
            <div className="px-4 pt-3 pb-2 border-b border-white/10">
              <select
                value={globalProperty.selectedPropertyId || ''}
                onChange={(e) => globalProperty.selectProperty(e.target.value)}
                className="w-full bg-white/10 text-white text-sm rounded-md px-3 py-2 border border-white/20 focus:border-amber-400 focus:outline-none appearance-none cursor-pointer"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center' }}
                data-testid="global-property-selector"
              >
                {globalProperty.properties.map(p => (
                  <option key={p.id} value={p.id} className="text-black">{p.nombre}</option>
                ))}
              </select>
            </div>
          )}

          {/* Admin Section Label */}
          {isAdmin && (
            <div className="px-4 pt-4">
              <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">
                Administrador
              </span>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {/* Admin Navigation */}
            {isAdmin && (
              <>
                {adminNavigation.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.href);
                  
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      data-testid={item.testId}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-md transition-all duration-200 ${
                        active
                          ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                          : 'text-amber-300/70 hover:bg-amber-500/10 hover:text-amber-300'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="font-medium">{item.name}</span>
                      <Badge className="ml-auto bg-amber-500 text-white text-[10px]">Admin</Badge>
                    </Link>
                  );
                })}
                <div className="my-3 border-t border-white/10" />
                <div className="px-4 pb-2">
                  <span className="text-xs font-semibold text-white/50 uppercase tracking-wider">
                    {isApartment ? 'Gestión de Apartamentos' : 'Gestión de Propiedades'}
                  </span>
                </div>
              </>
            )}

            {/* Owner Navigation */}
            {ownerNavigation.map((item, index) => renderMenuItem(item, index))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-4 px-2">
              <div className={`w-10 h-10 bg-white/15 rounded-full flex items-center justify-center text-white font-bold`}>
                {user?.nombre?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {user?.nombre || 'Usuario'}
                </p>
                <p className="text-xs text-white/60 truncate">
                  {user?.email}
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-5 w-5 mr-3" />
              Cerrar Sesión
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen">
        <main className="flex-1 p-6 lg:p-8 pt-16 lg:pt-8 overflow-auto">
          {/* Shift Guard - Block content if no active shift (non-admin only) */}
          {!isAdmin && shiftChecked && !activeShift && shiftPropertyId ? (
            <div className="flex items-center justify-center min-h-[70vh]" data-testid="shift-guard-overlay">
              <Card className="w-full max-w-md shadow-xl">
                <CardHeader className="text-center pb-2">
                  <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <LockKeyhole className="h-8 w-8 text-amber-600" />
                  </div>
                  <CardTitle className="text-2xl" style={{fontFamily: 'Manrope'}}>Apertura de Caja</CardTitle>
                  <p className="text-muted-foreground mt-2">
                    Debe abrir un turno para acceder al sistema
                  </p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                    <Clock className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-800">
                      Todas las operaciones requieren un turno activo para registrar la trazabilidad.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label>Notas del turno (opcional)</Label>
                    <Textarea
                      placeholder="Ej: Turno mañana, caja inicial $500.000..."
                      value={shiftNotas}
                      onChange={(e) => setShiftNotas(e.target.value)}
                      rows={2}
                      data-testid="shift-notas-input"
                    />
                  </div>
                  <Button
                    className="w-full bg-slate-800 hover:bg-slate-700 h-12 text-base"
                    onClick={handleOpenShift}
                    disabled={openingShift}
                    data-testid="open-shift-btn"
                  >
                    {openingShift ? 'Abriendo turno...' : (
                      <span className="flex items-center gap-2">
                        <DollarSign className="h-5 w-5" />
                        Abrir Turno / Apertura de Caja
                      </span>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </div>
          ) : (
            <>
              {/* Active Shift Banner */}
              {!isAdmin && activeShift && (
                <div className="mb-4 flex items-center justify-between bg-sky-50 border border-sky-200 rounded-lg px-4 py-2" data-testid="active-shift-banner">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-sky-600" />
                    <span className="text-sm text-sky-800">
                      <strong>Turno activo</strong> desde {new Date(activeShift.opened_at).toLocaleString('es-CO')} &middot; {activeShift.user_name}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50"
                    onClick={handleCloseShift}
                    data-testid="close-shift-btn"
                  >
                    Cerrar Turno
                  </Button>
                </div>
              )}
              {children}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
